import { formatDateNumberShort } from '@seen/core/dates'
import { featureFlags } from '@seen/core/featureFlags'
import { ConditionClinicalStatus } from '@seen/core/models/conditions/constants'
import { Diagnosis } from '@seen/core/models/conditions/Diagnosis'
import type { Participant } from '@seen/core/models/patients/participant'
import { getReferenceString } from '@seen/core/references'
import { DotSeparator, type ReorderItem, ReorderList } from '@seen/ui'
import { type JSX, useCallback, useMemo, useRef } from 'react'
import { ConditionCard } from '#src/components/condition/ConditionCard.tsx'
import { ConditionsCombobox } from '#src/components/ConditionsCombobox.tsx'
import { HoverCard } from '#src/components/core/hover-card/HoverCard.tsx'
import { HoverCardContent } from '#src/components/core/hover-card/HoverCardContent.tsx'
import { HoverCardTrigger } from '#src/components/core/hover-card/HoverCardTrigger.tsx'
import { TextLink } from '#src/components/core/TextLink.tsx'
import { IconCondition } from '#src/components/icons/IconCondition.tsx'
import {
  ConditionsCardSort,
  type ConditionsCardSortProps,
} from '#src/components/patient/detail-content/ConditionsCardSort.tsx'
import { useFacesheetCard } from '#src/components/patient/detail-content/facesheet/FacesheetCardContext.ts'
import {
  FacesheetCard,
  FacesheetCardActions,
  FacesheetCardAddButton,
  FacesheetCardCollapseTrigger,
  FacesheetCardContent,
  FacesheetCardCount,
  FacesheetCardHeader,
  FacesheetCardIcon,
  FacesheetCardLabel,
  FacesheetCardTitle,
} from '#src/components/patient/detail-content/facesheet/index.ts'
import {
  FACESHEET_CARD_LIST_CLASS,
  FACESHEET_CARD_ROW_CLASS,
  FacesheetCardSection,
  FacesheetCardSections,
} from '#src/components/patient/detail-content/FacesheetCardSection.tsx'
import {
  CLINICAL_HOVER_CARD_WIDTH,
  useAdaptiveHoverCardPlacement,
} from '#src/hooks/useAdaptiveHoverCardPlacement.ts'
import { useFacesheetConditionSortStore } from '#src/hooks/useFacesheetConditionSortStore.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useOrderedConditionsList } from '#src/hooks/useOrderedConditions.ts'
import { conditionSecondaryName } from '#src/utils/conditionSecondaryName.ts'

function referenceStrings(conditions: Diagnosis[]): string[] {
  return conditions.map((condition) => getReferenceString(condition.reference))
}

export interface ConditionsCardProps {
  participant: Participant
  defaultOpen?: boolean
}

export function ConditionsCard({
  participant,
  defaultOpen,
}: ConditionsCardProps): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const facesheetImprovements = useFeatureFlag(
    featureFlags.facesheetImprovements
  )
  const sortMode = useFacesheetConditionSortStore((state) => state.sortMode)
  const chronologicalDirection = useFacesheetConditionSortStore(
    (state) => state.chronologicalDirection
  )
  const alphabeticalDirection = useFacesheetConditionSortStore(
    (state) => state.alphabeticalDirection
  )
  const setSortMode = useFacesheetConditionSortStore(
    (state) => state.setSortMode
  )
  const setChronologicalDirection = useFacesheetConditionSortStore(
    (state) => state.setChronologicalDirection
  )
  const setAlphabeticalDirection = useFacesheetConditionSortStore(
    (state) => state.setAlphabeticalDirection
  )
  // With facesheet-improvements on, anatomical is the body-system order alone:
  // no drag to reorder, and the saved manual order is never read.
  const { orderedConditions, updateOrdering } = useOrderedConditionsList(
    participant.reference,
    { manualOrder: !facesheetImprovements }
  )
  const sortDirection =
    sortMode === 'alphabetical' ? alphabeticalDirection : chronologicalDirection

  // Every current condition is either controlled or not, so the two section
  // counts always add up to the card's count.
  const { activeConditions, controlledConditions } = useMemo(() => {
    const active = orderedConditions.filter(
      (condition) =>
        condition.clinicalStatus !== ConditionClinicalStatus.CONTROLLED
    )
    const controlled = orderedConditions.filter(
      (condition) =>
        condition.clinicalStatus === ConditionClinicalStatus.CONTROLLED
    )
    return {
      activeConditions: Diagnosis.sortForFacesheet(
        active,
        sortMode,
        sortDirection
      ),
      controlledConditions: Diagnosis.sortForFacesheet(
        controlled,
        sortMode,
        sortDirection
      ),
    }
  }, [orderedConditions, sortDirection, sortMode])

  const toReorderItems = useCallback(
    (conditions: Diagnosis[]): ReorderItem[] =>
      conditions.map((condition) => ({
        id: condition.reference.reference,
        component: (
          <ConditionRow condition={condition} participant={participant} />
        ),
      })),
    [participant]
  )

  // The stored ordering is always the active conditions followed by the
  // controlled ones, so a section's reorder is the two section orders
  // concatenated.
  const reorderActive = useCallback(
    (newOrder: string[]): void => {
      updateOrdering([...newOrder, ...referenceStrings(controlledConditions)])
    },
    [controlledConditions, updateOrdering]
  )

  const reorderControlled = useCallback(
    (newOrder: string[]): void => {
      updateOrdering([...referenceStrings(activeConditions), ...newOrder])
    },
    [activeConditions, updateOrdering]
  )

  const reorderable = !facesheetImprovements && sortMode === 'anatomical'
  const showSort = orderedConditions.length > 0

  return (
    <FacesheetCard
      defaultOpen={defaultOpen}
      collapsible={orderedConditions.length > 0}
      rumName="conditions"
    >
      <FacesheetCardHeader
        className={showSort ? 'flex-col items-stretch gap-2' : undefined}
      >
        <div className="flex items-center justify-between gap-2">
          <FacesheetCardTitle>
            <FacesheetCardIcon>
              <IconCondition />
            </FacesheetCardIcon>
            <FacesheetCardLabel>
              <TextLink to={`${participant.relativeUrl}/conditions`}>
                Conditions
              </TextLink>
            </FacesheetCardLabel>
            <FacesheetCardCollapseTrigger />
          </FacesheetCardTitle>
          <FacesheetCardActions>
            <FacesheetCardCount>{orderedConditions.length}</FacesheetCardCount>
            {!isReadOnly && (
              <ConditionsCombobox patientRef={participant.reference}>
                <FacesheetCardAddButton aria-label="Add condition" />
              </ConditionsCombobox>
            )}
          </FacesheetCardActions>
        </div>
        {showSort && (
          <ConditionsCardHeaderSort
            mode={sortMode}
            onModeChange={setSortMode}
            direction={sortDirection}
            onDirectionChange={
              sortMode === 'alphabetical'
                ? setAlphabeticalDirection
                : setChronologicalDirection
            }
          />
        )}
      </FacesheetCardHeader>
      {orderedConditions.length > 0 && (
        <FacesheetCardContent>
          <FacesheetCardSections>
            {activeConditions.length > 0 && (
              <FacesheetCardSection
                title="Active"
                count={activeConditions.length}
                dotClassName="bg-emerald-500"
              >
                <ConditionSectionList
                  conditions={activeConditions}
                  participant={participant}
                  reorderable={reorderable}
                  onReorder={reorderActive}
                  toReorderItems={toReorderItems}
                />
              </FacesheetCardSection>
            )}
            {controlledConditions.length > 0 && (
              <FacesheetCardSection
                title="Controlled"
                count={controlledConditions.length}
                dotClassName="bg-amber-500"
              >
                <ConditionSectionList
                  conditions={controlledConditions}
                  participant={participant}
                  reorderable={reorderable}
                  onReorder={reorderControlled}
                  toReorderItems={toReorderItems}
                />
              </FacesheetCardSection>
            )}
          </FacesheetCardSections>
        </FacesheetCardContent>
      )}
    </FacesheetCard>
  )
}

function ConditionsCardHeaderSort(
  props: ConditionsCardSortProps
): JSX.Element | null {
  const { open } = useFacesheetCard()
  if (!open) {
    return null
  }
  return <ConditionsCardSort {...props} />
}

function ConditionSectionList({
  conditions,
  participant,
  reorderable,
  onReorder,
  toReorderItems,
}: {
  conditions: Diagnosis[]
  participant: Participant
  reorderable: boolean
  onReorder: (orderedIds: string[]) => void
  toReorderItems: (conditions: Diagnosis[]) => ReorderItem[]
}): JSX.Element {
  if (reorderable) {
    return (
      <ReorderList
        items={toReorderItems(conditions)}
        onChange={onReorder}
        className={FACESHEET_CARD_LIST_CLASS}
        dragHandle
      />
    )
  }

  return (
    <div className={FACESHEET_CARD_LIST_CLASS}>
      {conditions.map((condition) => (
        <ConditionRow
          key={getReferenceString(condition.reference)}
          condition={condition}
          participant={participant}
        />
      ))}
    </div>
  )
}

function ConditionRow({
  condition,
  participant,
}: {
  condition: Diagnosis
  participant: Participant
}): JSX.Element {
  const secondaryName = conditionSecondaryName(condition)
  const viewportLayout = useFeatureFlag(featureFlags.hoverCardViewportLayout)
  const adaptivePlacement = useFeatureFlag(
    featureFlags.hoverCardAdaptivePlacement
  )
  const rowRef = useRef<HTMLParagraphElement>(null)
  const {
    side: adaptiveSide,
    sideOffset: adaptiveSideOffset,
    onOpenChange,
    cardRef,
  } = useAdaptiveHoverCardPlacement(rowRef, CLINICAL_HOVER_CARD_WIDTH)
  return (
    <HoverCard
      openDelay={300}
      closeDelay={200}
      onOpenChange={adaptivePlacement ? onOpenChange : undefined}
    >
      <HoverCardTrigger asChild>
        {/* Radix anchors to the trigger's border box. The row spans the
            column so long names wrap at the card width. Under side="bottom"
            that left edge never moves; adaptive placement still needs the
            full column to measure the right edge. */}
        <p ref={rowRef} className={FACESHEET_CARD_ROW_CLASS}>
          <TextLink
            to={`${participant.relativeUrl}/conditions/${condition.id}`}
          >
            {condition.name}
          </TextLink>
          {secondaryName && (
            <span className="select-none">
              <DotSeparator />
              <span className="text-stone-500">{secondaryName}</span>
            </span>
          )}
          {condition.onsetDateString && (
            <span className="select-none">
              <DotSeparator />
              <span className="text-stone-500">
                {formatDateNumberShort(condition.onsetDateString)}
              </span>
            </span>
          )}
        </p>
      </HoverCardTrigger>
      <HoverCardContent
        ref={adaptivePlacement ? cardRef : undefined}
        side={
          adaptivePlacement ? adaptiveSide : viewportLayout ? 'bottom' : 'right'
        }
        sideOffset={adaptivePlacement ? adaptiveSideOffset : undefined}
        align="start"
        scrollChainAnchor={adaptivePlacement ? rowRef : undefined}
        className="w-auto"
      >
        <ConditionCard
          condition={condition}
          to={`${participant.relativeUrl}/conditions/${condition.id}`}
        />
      </HoverCardContent>
    </HoverCard>
  )
}
